import React, {Component} from 'react';
import * as R from 'ramda';
import moment from 'moment';
import t from 'tcomb-form';
import classNames from 'classnames';

// Icons
import yesActiveImg from 'images/publisher/profile/yes-active.png';
import yesInactiveImg from 'images/publisher/profile/yes-inactive.png';
import noActiveImg from 'images/publisher/profile/no-active.png';
import noInactiveImg from 'images/publisher/profile/no-inactive.png';

import 'scss/form/component/form_pets';

const mapIndexed = R.addIndex(R.map);

export default class FormPets extends Component {
    constructor(props) {
        super(props);

        const pets = props.value;
        const hasAnswered = !R.isNil(props.value);
        const hasCat = R.filter(R.propEq('type', 'cat'), props.value).length > 0;
        const hasDog = R.filter(R.propEq('type', 'dog'), props.value).length > 0;
        const hasOther = R.filter(R.propEq('type', 'other'), props.value).length > 0;
        const hasPets = hasAnswered ? R.length(props.value) > 0 : false;

        this.state = {
            pets,
            hasAnswered,
            hasPets,
            hasDog,
            hasCat,
            hasOther,
            hasError: false
        };
    }

    handleYearBornSelect = (e, idx, type) => {
        const {pets} = this.state;
        const specifiedPets = R.filter(R.propEq('type', type), pets);
        const nonspecifiedPets = R.reject(R.propEq('type', type), pets);

        const newPets = R.adjust(
            R.assocPath(['year_born'], parseInt(e.target.value, 10)),
            idx,
            specifiedPets
        );

        this.setState(
            {
                pets: R.concat(nonspecifiedPets, newPets)
            },
            this.onChange
        );
    };

    addCat = () => {
        const {pets} = this.state;

        const newPet = [{type: 'cat', year_born: null}];

        this.setState(
            {
                pets: R.concat(pets, newPet)
            },
            this.onChange
        );
    };

    addDog = () => {
        const {pets} = this.state;

        const newPet = [{type: 'dog', year_born: null}];

        this.setState(
            {
                pets: R.concat(pets, newPet)
            },
            this.onChange
        );
    };

    removePet = (idx, type) => {
        const {pets} = this.state;
        const specifiedPets = R.filter(R.propEq('type', type), pets);
        const nonspecifiedPets = R.reject(R.propEq('type', type), pets);

        const newPets = [...specifiedPets.slice(0, idx), ...specifiedPets.slice(idx + 1)];

        this.setState(
            {
                pets: R.concat(nonspecifiedPets, newPets)
            },
            this.onChange
        );
    };

    onChange = () => {
        this.props.onChange(this.state.pets, this.props.ctx.path);
    };

    getPetsData = () => {
        const {hasPets, hasDog, hasOther, hasCat, pets} = this.state;
        let totalPets = [];

        if (R.isNil(pets)) {
            return [];
        } else if (!hasPets) {
            return [];
        }

        if (hasDog) {
            const dogs = R.filter(R.propEq('type', 'dog'), pets);
            totalPets = totalPets.concat(dogs);
        }
        if (hasCat) {
            const cats = R.filter(R.propEq('type', 'cat'), pets);
            totalPets = totalPets.concat(cats);
        }
        if (hasOther) {
            const others = R.filter(R.propEq('type', 'other'), pets);
            totalPets = totalPets.concat(others);
        }
        return totalPets;
    };

    getValidationOptions = () => ({
        path: this.props.ctx.path,
        context: t.mixin(t.mixin({}, this.props.context || this.props.ctx.context), {
            options: this.props.options
        })
    });

    validate = () => {
        const petData = this.getPetsData();
        const result = t.validate(petData, this.props.type, this.getValidationOptions());

        this.setState({
            hasError: !result.isValid()
        });

        return result;
    };

    getIconImgs = () => {
        const {hasAnswered, hasPets} = this.state;

        let yesIconImg;
        let noIconImg;

        if (hasAnswered) {
            yesIconImg = hasPets ? yesActiveImg : yesInactiveImg;
            noIconImg = !hasPets ? noActiveImg : noInactiveImg;
        } else {
            yesIconImg = yesInactiveImg;
            noIconImg = noInactiveImg;
        }

        return [yesIconImg, noIconImg];
    };

    setHasPets = hasPets => {
        this.setState(
            {
                hasPets,
                pets: hasPets ? this.state.pets : []
            },
            this.onChange
        );
    };

    toggleHasPet = petType => {
        const {pets} = this.state;

        const totalPets = R.isNil(pets) ? [] : pets;
        let newPets = [];
        if (
            R.isNil(pets) ||
            R.isEmpty(pets) ||
            R.isEmpty(R.filter(R.propEq('type', petType), totalPets))
        ) {
            newPets = [{type: petType}];
        }

        this.toggleHasPetStatus(petType);
        this.setState(
            {
                pets: newPets.concat(totalPets)
            },
            this.onChange
        );
    };

    toggleHasPetStatus = petType => {
        const {hasOther, hasCat, hasDog} = this.state;
        let petConditional;
        switch (petType) {
            case 'cat':
                petConditional = {hasCat: !hasCat};
                break;
            case 'dog':
                petConditional = {hasDog: !hasDog};
                break;
            default:
                petConditional = {hasOther: !hasOther};
        }
        this.setState(petConditional);
    };

    displayYesNoSelection = () => {
        const {hasAnswered, hasPets} = this.state;

        const yesButtonClasses = classNames('selection', 'selection-yes', {
            selected: hasAnswered && hasPets
        });
        const noButtonClasses = classNames('selection', 'selection-no', {
            selected: hasAnswered && !hasPets
        });
        const [yesIconImg, noIconImg] = this.getIconImgs();

        return (
            <div className="pets-selection">
                <div
                    className={yesButtonClasses}
                    onClick={() => {
                        this.setHasPets(true);
                    }}
                >
                    <img className="selection-icon" src={yesIconImg} />
                    <div className="selection-text">YES</div>
                </div>
                <div
                    className={noButtonClasses}
                    onClick={() => {
                        this.setHasPets(false);
                    }}
                >
                    <img className="selection-icon" src={noIconImg} />
                    <div className="selection-text">NO</div>
                </div>
            </div>
        );
    };

    yearOptions = () => {
        const year = moment().get('year');
        return R.pipe(
            mapIndexed((i, index) => (
                <option key={index} value={i}>
                    {index}
                </option>
            )),
            R.reverse,
            R.prepend(<option>Select Year</option>)
        )(R.reverse(R.range(year - 20, year + 1)));
    };

    displayPetsSelects = type => {
        const {pets} = this.state;

        if (R.isNil(pets)) return;

        return mapIndexed((pet, idx) => {
            const removePetButton =
                pets.length > 1 ? (
                    <i
                        onClick={() => this.removePet(idx, pet.type)}
                        className="fa fa-trash-o remove-pet"
                        aria-hidden="true"
                    />
                ) : null;

            return (
                <div>
                    <div className="pet-year-dropdown">
                        <div className="pet-info">
                            <label className="control-label pet-born-prompt">
                                {`${pet.type} ${idx + 1}`}
                            </label>
                            {removePetButton}
                        </div>
                        <div className="pet-age">{`How old is your ${pet.type}?`}</div>

                        <select
                            id="year-born-select"
                            onChange={e => this.handleYearBornSelect(e, idx, pet.type)}
                            defaultValue={pet.year_born}
                            value={pet.year_born}
                            key={`year-born-${idx}`}
                        >
                            {this.yearOptions()}
                        </select>
                    </div>
                </div>
            );
        }, R.filter(R.propEq('type', type), pets));
    };

    displayPetsDetails = () => {
        const {hasPets, hasDog, hasCat, hasOther} = this.state;

        const catsSelects = this.displayPetsSelects('cat');
        const dogsSelects = this.displayPetsSelects('dog');

        const petType = (
            <div className="form-content-specialty">
                <label className="control-label">What type of pets do you own?</label>

                <div className="content-specialty-selection">
                    <div className="content-specialty-select">
                        <input
                            className="content-specialty-checkbox"
                            type="checkbox"
                            name="hasCat"
                            checked={hasCat}
                            onChange={() => this.toggleHasPet('cat')}
                        />
                        <div className="content-specialty-text">Cat</div>
                    </div>
                    <div className="content-specialty-select">
                        <input
                            className="content-specialty-checkbox"
                            type="checkbox"
                            name="hasDog"
                            checked={hasDog}
                            onChange={() => this.toggleHasPet('dog')}
                        />
                        <div className="content-specialty-text">Dog</div>
                    </div>
                    <div className="content-specialty-select">
                        <input
                            className="content-specialty-checkbox"
                            type="checkbox"
                            name="hasOther"
                            checked={hasOther}
                            onChange={() => this.toggleHasPet('other')}
                        />
                        <div className="content-specialty-text">Other</div>
                    </div>
                </div>
            </div>
        );

        const cat = hasCat ? (
            <div>
                {catsSelects}

                <div className="add-another-pet" onClick={this.addCat}>
                    + Add another cat
                </div>
            </div>
        ) : null;

        const dog = hasDog ? (
            <div>
                {dogsSelects}

                <div className="add-another-pet" onClick={this.addDog}>
                    + Add another dog
                </div>
            </div>
        ) : null;

        return hasPets ? (
            <div className="pets-details">
                {petType}
                {dog}
                {cat}
            </div>
        ) : null;
    };

    render() {
        const {hasError} = this.state;
        const label = this.props.options.label || this.props.ctx.label;

        const selection = this.displayYesNoSelection();
        const petsDetails = this.displayPetsDetails();

        return (
            <div className={classNames('form-group', hasError && 'has-error')}>
                <div className="form-pets">
                    <label className="control-label">{label}</label>

                    {selection}
                    {petsDetails}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./form_components/form_pets.js